import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createTender, updateTender, getTender, getTenderOptions } from '../services/api';
import { useToast } from '../context/ToastContext';
import './TenderForm.css';

function TenderForm({ tender, options: initialOptions, onSuccess, onClose }) {
  const { toast } = useToast();
  const isEditing = !!tender;

  const [options, setOptions] = useState(initialOptions || { sources: [], statuses: [], buyers: [], products: [] });
  const [formData, setFormData] = useState({
    source: 'gem',
    external_tender_id: '',
    buyer_id: '',
    title: '',
    description: '',
    publish_date: '',
    submission_deadline: '',
    emd_amount: '',
    tender_value: '',
    status: 'discovered',
    portal_url: '',
  });
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const successTimeoutRef = useRef(null);
  const [optionsLoading, setOptionsLoading] = useState(!initialOptions);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Unique brands from products (for brand-first dropdown)
  const brands = useMemo(() => {
    const seen = new Set();
    return (options.products || [])
      .filter(p => p.brand_id && !seen.has(p.brand_id) && seen.add(p.brand_id))
      .map(p => ({ value: p.brand_id, label: p.brand_name || p.brand_id }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [options.products]);

  // Fetch options if not provided
  useEffect(() => {
    const fetchOptions = async () => {
      if (initialOptions) return;
      
      try {
        setOptionsLoading(true);
        const response = await getTenderOptions();
        if (response.success) {
          setOptions(response.options);
        }
      } catch (err) {
        setError('Failed to load form options');
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, [initialOptions]);

  // Fetch full tender data if editing
  useEffect(() => {
    const fetchTenderData = async () => {
      if (!tender?.id) return;
      
      try {
        const response = await getTender(tender.id);
        if (response.success) {
          const t = response.tender;
          setFormData({
            source: t.source || 'gem',
            external_tender_id: t.external_tender_id || '',
            buyer_id: t.buyer_id || '',
            title: t.title || '',
            description: t.description || '',
            publish_date: t.publish_date ? t.publish_date.split('T')[0] : '',
            submission_deadline: t.submission_deadline ? t.submission_deadline.split('T')[0] : '',
            emd_amount: t.emd_amount || '',
            tender_value: t.tender_value || '',
            status: t.status || 'discovered',
            portal_url: t.portal_url || '',
          });
          setSelectedProducts((t.products || []).map(p => ({ ...p, brand_id: p.brand_id || '' })));
        }
      } catch (err) {
        setError('Failed to load tender data');
      }
    };
    fetchTenderData();
  }, [tender]);

  useEffect(() => {
    return () => { if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current); };
  }, []);

  // When options.products or selectedProducts load, fill brand_id for rows that have product_id (e.g. when editing)
  useEffect(() => {
    if (!options.products?.length) return;
    setSelectedProducts(prev => {
      const next = prev.map(row => {
        if (row.brand_id) return row;
        const product = options.products.find(pr => pr.value === row.product_id);
        return product ? { ...row, brand_id: product.brand_id || '' } : row;
      });
      const hasChange = next.some((row, i) => row.brand_id !== (prev[i]?.brand_id));
      return hasChange ? next : prev;
    });
  }, [options.products, selectedProducts]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
    setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
  };

  // Add product (brand first, then product)
  const addProduct = () => {
    setSelectedProducts(prev => [...prev, { brand_id: '', product_id: '', quantity: 1 }]);
  };

  // Update product row
  const updateProduct = (index, field, value) => {
    setSelectedProducts(prev => prev.map((p, i) => {
      if (i !== index) return p;
      if (field === 'brand_id') {
        return { ...p, brand_id: value, product_id: '', product_name: '', brand_name: '' };
      }
      if (field === 'product_id') {
        const product = options.products?.find(pr => pr.value === value);
        return {
          ...p,
          product_id: value,
          product_name: product?.model || '',
          brand_name: product?.brand_name || '',
        };
      }
      return { ...p, [field]: value };
    }));
  };

  // Products filtered by selected brand for a row
  const productsForBrand = (brandId) => {
    if (!brandId) return [];
    return (options.products || []).filter(p => p.brand_id === brandId);
  };

  // Remove product
  const removeProduct = (index) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.source) errors.source = 'Tender source is required';
    setFieldErrors(errors);
    if (Object.keys(errors).length) return false;
    return true;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validateForm()) return;

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        products: selectedProducts.filter(p => p.product_id).map(p => ({
          product_id: p.product_id,
          quantity: parseInt(p.quantity) || 1,
        })),
      };

      let response;
      if (isEditing) {
        response = await updateTender(tender.id, submitData);
      } else {
        response = await createTender(submitData);
      }

      if (response.success) {
        setLoading(false);
        setSubmitSuccess(true);
        toast.success(isEditing ? 'Tender updated' : 'Tender created');
        successTimeoutRef.current = setTimeout(() => onSuccess(), 1200);
        return;
      }
    } catch (err) {
      if (err.details && typeof err.details === 'object') {
        setFieldErrors(err.details);
      } else {
        setError(err.message || 'Failed to save tender');
      }
    } finally {
      setLoading(false);
    }
  };

  if (optionsLoading) {
    return (
      <div className="modal-overlay">
        <div className="tender-form-modal">
          <div className="form-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="tender-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Tender' : 'Add New Tender'}</h2>
          <button className="close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="tender-form">
          {error && (
            <div className="form-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {error}
            </div>
          )}

          {/* Source and Status Section */}
          <div className="form-section">
            <h3 className="section-title">
              <span className="section-title-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </span>
              Basic Information
            </h3>
            
            <div className="form-row">
              <div className="form-group field-required">
                <label htmlFor="source">Tender Source</label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  aria-invalid={!!fieldErrors.source}
                >
                  {options.sources?.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                {fieldErrors.source && <span className="field-error" role="alert">{fieldErrors.source}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  {options.statuses?.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="external_tender_id">External Tender ID</label>
              <input
                type="text"
                id="external_tender_id"
                name="external_tender_id"
                value={formData.external_tender_id}
                onChange={handleChange}
                placeholder="Original ID from portal (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="title">Tender Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Brief description of the tender"
              />
            </div>

            <div className="form-group">
              <label htmlFor="buyer_id">Department / Buyer</label>
              <select
                id="buyer_id"
                name="buyer_id"
                value={formData.buyer_id}
                onChange={handleChange}
              >
                <option value="">Select from client list...</option>
                {options.buyers?.map(b => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates Section */}
          <div className="form-section">
            <h3 className="section-title">
              <span className="section-title-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </span>
              Dates
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="publish_date">Publish Date</label>
                <input
                  type="date"
                  id="publish_date"
                  name="publish_date"
                  value={formData.publish_date}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="submission_deadline">Submission Deadline</label>
                <input
                  type="date"
                  id="submission_deadline"
                  name="submission_deadline"
                  value={formData.submission_deadline}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Financial Section */}
          <div className="form-section">
            <h3 className="section-title">
              <span className="section-title-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </span>
              Financial Details
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="emd_amount">EMD Amount (₹)</label>
                <input
                  type="number"
                  id="emd_amount"
                  name="emd_amount"
                  value={formData.emd_amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tender_value">Estimated Value (₹)</label>
                <input
                  type="number"
                  id="tender_value"
                  name="tender_value"
                  value={formData.tender_value}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="form-section">
            <h3 className="section-title">
              <span className="section-title-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </span>
              Products Required
            </h3>
            
            <div className="products-list">
              {selectedProducts.map((product, index) => (
                <div key={index} className="product-row">
                  <select
                    value={product.brand_id ?? ''}
                    onChange={(e) => updateProduct(index, 'brand_id', e.target.value)}
                    className="product-select brand-select"
                    title="Select brand first"
                  >
                    <option value="">Select brand...</option>
                    {brands.map(b => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                  <select
                    value={product.product_id}
                    onChange={(e) => updateProduct(index, 'product_id', e.target.value)}
                    className="product-select"
                    title={product.brand_id ? 'Select product' : 'Select a brand first'}
                    disabled={!product.brand_id}
                  >
                    <option value="">Select product...</option>
                    {productsForBrand(product.brand_id).map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={product.quantity}
                    onChange={(e) => updateProduct(index, 'quantity', e.target.value)}
                    placeholder="Qty"
                    min="1"
                    className="quantity-input"
                  />
                  <button
                    type="button"
                    className="remove-product-btn"
                    onClick={() => removeProduct(index)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            
            <button 
              type="button"
              className="add-product-btn"
              onClick={addProduct}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Product
            </button>
          </div>

          {/* Additional Info */}
          <div className="form-section">
            <h3 className="section-title">
              <span className="section-title-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </span>
              Additional Information
            </h3>
            
            <div className="form-group">
              <label htmlFor="portal_url">Portal URL</label>
              <input
                type="url"
                id="portal_url"
                name="portal_url"
                value={formData.portal_url}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Notes / Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Additional notes about the tender..."
                rows={3}
              />
            </div>
          </div>

          <div className="form-actions form-actions-sticky">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={submitSuccess}>
              Cancel
            </button>
            <button type="submit" className={`submit-btn ${loading ? 'btn-loading' : ''} ${submitSuccess ? 'btn-success-state' : ''}`} disabled={loading || submitSuccess}>
              {loading ? (
                <span className="btn-spinner"></span>
              ) : submitSuccess ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden>
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Saved
                </>
              ) : isEditing ? (
                'Update Tender'
              ) : (
                'Add Tender'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TenderForm;
